// ============================================================================
// DYNAMIC SECURITIES ENGINE
// Maintains a seed universe of ~400 NYSE/NASDAQ tickers used as:
//   1. Metadata lookup (name, category, colour) for the full Polygon market scan
//   2. Fallback watchlist when Polygon grouped-daily is unavailable
//
// Refreshes every 15 minutes (cached between refreshes). No external API calls
// are made during refresh — ranking is handled by the signal engine using
// Polygon's grouped daily endpoint.
// ============================================================================

// ── SEED UNIVERSE: large pool per category to pick from ──
const NYSE_UNIVERSE = {
  megaCapTech: {
    label: "Mega-Cap Technology", color: "#6366f1",
    tickers: [
      "AAPL","MSFT","GOOGL","AMZN","META","NVDA","TSLA","AVGO","ORCL","CRM",
      "AMD","ADBE","INTC","QCOM","TXN","NOW","IBM","AMAT","MU","PLTR",
      "PANW","SNPS","CDNS","KLAC","LRCX","MRVL","FTNT","CRWD","TEAM","ZS",
      "DDOG","SNOW","NET","WDAY","ANSS","NXPI","ON","MPWR","MCHP","SWKS",
    ],
  },
  indexETFs: {
    label: "Index ETFs", color: "#10b981",
    tickers: [
      "SPY","QQQ","IWM","DIA","VOO","VTI","IVV","VEA","EFA","VWO",
      "AGG","BND","TLT","LQD","HYG","SCHD","VIG","VXUS","VGK","EEM",
      "MDY","IJR","RSP","SPLG","SPTM","VT","ACWI","IEMG","SCZ","VNQ",
    ],
  },
  sectorETFs: {
    label: "Sector & Thematic ETFs", color: "#f59e0b",
    tickers: [
      "XLF","XLK","XLE","XLV","XLI","XLP","XLY","XLU","XLB","XLRE",
      "XLC","SMH","XBI","ARKK","KWEB","GDX","GLD","SLV","USO","IBIT",
      "SOXX","IGV","IYR","VHT","VFH","VIS","VDE","HACK","SKYY","BOTZ",
      "TAN","ICLN","LIT","CIBR","BLOK","BITQ","PAVE","JETS","XHB","ITB",
    ],
  },
  leveraged: {
    label: "Leveraged & Inverse", color: "#ef4444",
    tickers: [
      "TQQQ","SQQQ","SPXL","SPXS","SOXL","SOXS","UVXY","SVXY","TNA","TZA",
      "LABU","LABD","NUGT","DUST","JNUG","JDST","FAS","FAZ","TECL","TECS",
      "FNGU","FNGD","UPRO","SDS","QLD","QID","UWM","TWM","ERX","ERY",
    ],
  },
  financials: {
    label: "Financials", color: "#0ea5e9",
    tickers: [
      "JPM","BAC","WFC","GS","MS","C","BRK.B","V","MA","AXP",
      "BLK","SCHW","CME","ICE","CB","PGR","MMC","AON","USB","PNC",
      "TFC","FITB","KEY","CFG","MTB","HBAN","RF","ZION","CMA","ALLY",
      "COF","DFS","SYF","NDAQ","MSCI","SPGI","MCO","FIS","GPN","PYPL",
    ],
  },
  healthcare: {
    label: "Healthcare & Biotech", color: "#ec4899",
    tickers: [
      "UNH","JNJ","LLY","PFE","ABBV","MRK","TMO","ABT","DHR","BMY",
      "AMGN","GILD","ISRG","VRTX","SYK","MDT","REGN","ZTS","CI","ELV",
      "HCA","DXCM","IDXX","IQV","BSX","EW","BDX","A","HOLX","ALGN",
      "MRNA","BIIB","ILMN","EXAS","NBIX","SGEN","ALNY","RARE","BMRN","UTHR",
    ],
  },
  energy: {
    label: "Energy", color: "#f97316",
    tickers: [
      "XOM","CVX","COP","SLB","EOG","MPC","PSX","VLO","OXY","WMB",
      "KMI","HAL","DVN","FANG","NEE","PXD","HES","BKR","CTRA","EQT",
      "OKE","TRGP","LNG","DTM","AM","AR","RRC","SWN","CHK","MGY",
    ],
  },
  consumer: {
    label: "Consumer & Retail", color: "#8b5cf6",
    tickers: [
      "WMT","COST","HD","PG","KO","PEP","MCD","NKE","SBUX","TGT",
      "LOW","TJX","CL","MDLZ","PM","MO","EL","CMG","YUM","DG",
      "DLTR","ROST","ORLY","AZO","POOL","WSM","DECK","LULU","TPR","RL",
      "MNST","KDP","HSY","GIS","SJM","K","CAG","CPB","HRL","MKC",
    ],
  },
  industrials: {
    label: "Industrials & Defense", color: "#64748b",
    tickers: [
      "CAT","BA","HON","RTX","UNP","GE","LMT","DE","MMM","UPS",
      "FDX","NOC","GD","EMR","ETN","ITW","ROK","PH","IR","DOV",
      "AME","SWK","FAST","GNRC","VRSK","TT","WM","RSG","CARR","OTIS",
    ],
  },
  reits: {
    label: "REITs & Real Estate", color: "#14b8a6",
    tickers: [
      "PLD","AMT","CCI","EQIX","PSA","SPG","O","WELL","DLR","AVB",
      "EQR","VTR","ARE","ESS","MAA","UDR","REG","FRT","KIM","HST",
      "SUI","ELS","INVH","STAG","REXR","COLD","CUBE","LSI","PEAK","MPW",
    ],
  },
  communications: {
    label: "Communications & Media", color: "#a855f7",
    tickers: [
      "DIS","CMCSA","NFLX","T","VZ","TMUS","CHTR","WBD","SPOT","ROKU",
      "PARA","LYV","MTCH","ZG","IAC","TTWO","EA","RBLX","SNAP","PINS",
      "UBER","LYFT","ABNB","BKNG","EXPE","DASH","DKNG","PENN","CZR","MGM",
    ],
  },
  momentum: {
    label: "High Momentum / Volatility", color: "#f43f5e",
    tickers: [
      "COIN","MSTR","RIVN","LCID","NIO","SOFI","HOOD","SNAP","PINS","RBLX",
      "DKNG","PATH","U","SMCI","ARM","IONQ","RGTI","QUBT","SOUN","AI",
      "BBAR","MARA","RIOT","CLSK","BITF","CORZ","HUT","CIFR","SOS","WULF",
    ],
  },
};

// ── Active watchlist ──
let watchlist = null;
let lastRefresh = 0;
const REFRESH_INTERVAL = 15 * 60 * 1000;

// Build initial watchlist from seeds (no API calls, instant)
function buildInitialWatchlist() {
  const categories = [];
  const allSecurities = [];

  Object.entries(NYSE_UNIVERSE).forEach(([key, cat]) => {
    const items = cat.tickers.slice(0, 30).map(ticker => ({
      ticker,
      rawTicker: ticker,
      name: ticker,
      sector: cat.label,
      category: cat.label,
      categoryKey: key,
      categoryColor: cat.color,
      exchange: 'NYSE',
    }));
    categories.push({ key, label: cat.label, color: cat.color, count: items.length });
    allSecurities.push(...items);
  });

  return { categories, allSecurities };
}

// Refresh the full watchlist. The signal engine uses Polygon's full market
// scan directly — the curated list here is metadata lookup (names,
// categories, colours) and a fallback when Polygon is unavailable.
async function refreshWatchlist() {
  watchlist = buildInitialWatchlist();
  lastRefresh = Date.now();
  console.log(`[Securities] watchlist refreshed: ${watchlist.allSecurities.length} tickers`);
  return watchlist;
}

// ── Public API ──

export async function getActiveWatchlist() {
  if (watchlist && Date.now() - lastRefresh < REFRESH_INTERVAL) return watchlist;
  try {
    return await refreshWatchlist();
  } catch (e) {
    console.error('Watchlist refresh failed:', e);
    if (!watchlist) watchlist = buildInitialWatchlist();
    return watchlist;
  }
}

export function getCategories() {
  if (watchlist) return watchlist.categories;
  return buildInitialWatchlist().categories;
}

export function getAllSecurities() {
  if (watchlist) return watchlist.allSecurities;
  return buildInitialWatchlist().allSecurities;
}

export function getSecuritiesCount() {
  return getAllSecurities().length;
}

export function forceRefresh() {
  lastRefresh = 0;
  return getActiveWatchlist();
}

// Initialize on import
if (!watchlist) {
  watchlist = buildInitialWatchlist();
}
